/**
 * @file ProductAttributeRowItem.tsx
 * @description Строка характеристики товара (phase19.16 Р2а, SB33): ⋮⋮ для
 * перетаскивания, поля «Название» и «Значение», ✕. На телефоне поля друг под
 * другом, цели касания 48; от md — в одну строку (название уже значения).
 * Замечание показывается под строкой, когда фокус ушёл из неё (не мешаем
 * печатать).
 */

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, X } from "lucide-react";
import type { FocusEvent } from "react";

import { Input } from "../../../../components/ui/input";
import { cn } from "@/lib/utils";
import {
  ATTRIBUTE_KEY_MAX,
  ATTRIBUTE_VALUE_MAX,
  attributeKeyInputId,
  type ProductAttributeRow,
} from "../product-attributes";

interface Props {
  row: ProductAttributeRow;
  /** Номер строки с нуля — для подписей программам чтения экрана. */
  index: number;
  /** Замечание к строке (уже с учётом «фокус ушёл»); `null` — нет. */
  error: string | null;
  disabled?: boolean;
  onChange: (next: ProductAttributeRow) => void;
  onRemove: () => void;
  /** Фокус ушёл за пределы строки — можно показывать замечание. */
  onLeave: () => void;
}

/** Замечание относится к значению (иначе — к названию). */
function isValueError(error: string): boolean {
  return error.startsWith("Укажите значение") || error.startsWith("Значение");
}

export function ProductAttributeRowItem({ row, index, error, disabled, onChange, onRemove, onLeave }: Props) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.id,
    disabled,
  });
  const errorId = `pa-${row.id}-error`;
  const keyInvalid = !!error && !isValueError(error);
  const valueInvalid = !!error && isValueError(error);
  const n = index + 1;

  function handleBlur(e: FocusEvent<HTMLLIElement>) {
    // Фокус перешёл на другое поле этой же строки — строку не покинули.
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) onLeave();
  }

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      onBlur={handleBlur}
      className={cn("rounded-lg bg-surface-low", isDragging && "relative z-10 opacity-70 shadow-lg")}
    >
      <div className="flex items-start gap-1 p-1">
        <button
          type="button"
          {...attributes}
          {...listeners}
          disabled={disabled}
          aria-label={`Перетащить строку ${n}`}
          className="inline-flex size-12 shrink-0 cursor-grab touch-none items-center justify-center text-on-surface-variant disabled:cursor-not-allowed lg:size-10"
        >
          <GripVertical aria-hidden className="size-4" />
        </button>
        <div className="grid min-w-0 flex-1 grid-cols-1 gap-1 md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] md:gap-2">
          <Input
            id={attributeKeyInputId(row.id)}
            value={row.key}
            onChange={(e) => onChange({ ...row, key: e.target.value })}
            maxLength={ATTRIBUTE_KEY_MAX}
            placeholder="Название"
            aria-label={`Название характеристики ${n}`}
            aria-invalid={keyInvalid || undefined}
            aria-describedby={keyInvalid ? errorId : undefined}
            disabled={disabled}
            className="max-lg:h-12"
          />
          <Input
            value={row.value}
            onChange={(e) => onChange({ ...row, value: e.target.value })}
            maxLength={ATTRIBUTE_VALUE_MAX}
            placeholder="Значение"
            aria-label={`Значение характеристики ${n}`}
            aria-invalid={valueInvalid || undefined}
            aria-describedby={valueInvalid ? errorId : undefined}
            disabled={disabled}
            className="max-lg:h-12"
          />
        </div>
        <button
          type="button"
          onClick={onRemove}
          disabled={disabled}
          aria-label={`Удалить характеристику ${n}`}
          className="inline-flex size-12 shrink-0 items-center justify-center rounded-md text-on-surface-variant hover:text-destructive disabled:cursor-not-allowed lg:size-10"
        >
          <X aria-hidden className="size-4" />
        </button>
      </div>
      {error ? (
        <p id={errorId} className="type-caption pb-2 pl-[3.25rem] pr-3 text-destructive lg:pl-11">
          {error}
        </p>
      ) : null}
    </li>
  );
}
